/**
 * useLaneV2AutoOpen — opens the workspace on a task when its changeset is first proposed.
 *
 * Owner-approved exception to "the panel opens only on a click" (see `weaveWorkspaceStore`).
 * The rule lives in `changeTarget.shouldAutoOpen`; this hook only feeds it the session store's
 * snapshot edges and remembers which `task_id:package_hash` keys were already seen.
 *
 * It lands on the FIRST CHANGED DIFF (the task overview only when no component has a body to show): the panel is
 * where the changes are reviewed, so it opens on them.
 *
 * Everything present when a session attaches (and everything that arrives while the stream is not
 * `connected`) is seeded as seen, so reload, switching sessions and reconnecting never open the panel.
 * A key is consumed on first sight even when the panel stays shut (narrow screen, panel busy,
 * hidden tab): it must not pop open later for a changeset the reader already walked past.
 */
import { useEffect } from "react";
import { changesetKey, firstChangedWorkItemId, shouldAutoOpen } from "@/components/organisms/multi-agent/laneV2/changeTarget";
import { useLaneV2SessionStore } from "@/stores/laneV2SessionStore";
import type { TaskSnapshot } from "@/services/multi-agent/laneV2/types";
import { useWeaveWorkspaceStore } from "@/stores/weaveWorkspaceStore";

/** Wide enough for the chat column AND the panel side by side (owner 2026-10-14: raised from 1024px so the chat keeps its room). */
export const WIDE_QUERY = "(min-width: 1280px)";

function isWide(): boolean {
  return typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia(WIDE_QUERY).matches;
}

/**
 * Is the panel holding something the reader chose? The plan view THIS screen opened on its own for the same task (the plan put
 * up for approval, `openedBy: "auto"`) is not: the build's changed code replaces it, as before that plan view existed
 * (dev 2026-11-02, after #421 the code stopped opening). Anything the reader opened, or another task, is left alone.
 */
function panelTaken(taskId: string): boolean {
  const ws = useWeaveWorkspaceStore.getState();
  if (ws.section === null) return false;
  return !(ws.section === "task" && ws.openedBy === "auto" && ws.focusRef === taskId);
}

/** Subscribes to the session store; returns the unsubscribe. Exported so the edge logic is testable without a DOM. */
export function startLaneV2AutoOpen(): () => void {
  const seen = new Set<string>();
  let sessionId: string | null = null;

  const seed = (snapshots: Record<string, TaskSnapshot>) => {
    for (const snap of Object.values(snapshots)) {
      const key = changesetKey(snap);
      if (key) seen.add(key);
    }
  };
  const initial = useLaneV2SessionStore.getState();
  sessionId = initial.sessionId;
  seed(initial.snapshots);

  return useLaneV2SessionStore.subscribe((state, prev) => {
    if (state.sessionId !== sessionId) {
      sessionId = state.sessionId;
      seen.clear();
      seed(state.snapshots);
      return;
    }
    if (state.snapshots === prev.snapshots && state.streamStatus === prev.streamStatus) return;
    if (state.streamStatus !== "connected") {
      seed(state.snapshots);
      return;
    }
    for (const snap of Object.values(state.snapshots)) {
      const before = prev.snapshots[snap.task_id];
      if (before === snap) continue;
      const key = changesetKey(snap);
      if (!key || seen.has(key)) continue;
      const open = shouldAutoOpen(before, snap, {
        seen,
        connected: true,
        wide: isWide(),
        panelOpen: panelTaken(snap.task_id),
        visible: typeof document === "undefined" || document.visibilityState === "visible",
      });
      seen.add(key);
      if (open) useWeaveWorkspaceStore.getState().openTask(snap.task_id, firstChangedWorkItemId(snap), "auto");
    }
  });
}

export function useLaneV2AutoOpen(): void {
  useEffect(() => startLaneV2AutoOpen(), []);
}
